import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery as useConvexQuery, useAction } from "convex/react";
import { refs } from "../../convex/apiRefs";
import { decodeConvexDoc } from "../../convex/decodeConvexDoc";
import {
  BUCKETS,
  STATUSES,
  daysAnswer,
  jobOf,
  whenOf,
  pMulti,
  refPMulti,
  pct,
  filterRows,
  kpiTiles,
  gradeTiles,
  gradeCell,
  crewLine,
  timeBins,
  pMultiBins,
  latencyBins,
  sortRows,
  rowForHash,
} from "./jevViewerModel";
import { COLUMN_HELP, CHART_HELP, CARD_HELP, FIELD_HELP, CREW_COLUMN_HELP, tileHelp } from "./jevHelp";
import "./jev.css";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// JevViewer (2026-10-07) — the live Jev window body. Sky view (tiles, charts) →
// call list → one call → the raw JSON. A React port of PRED's viewer
// (predictive-scheduling scripts/jev_viewer.template.html), reading the Convex
// JevShadowLog mirror reactively, so new calls appear without a refresh.
// All logic that can be pure lives in jevViewerModel.js (tested there).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const BUCKET_COLORS = ["var(--jev-b1)", "var(--jev-b2)", "var(--jev-b3)", "var(--jev-b4)"];
const STATUS_STYLE = {
  ok: ["var(--jev-good)", "✓"],
  skipped: ["var(--jev-warning)", "–"],
  timeout: ["var(--jev-serious)", "⏱"],
  error: ["var(--jev-critical)", "✕"],
};
const fmtTime = (d) =>
  d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const binLabel = (d, hourly) =>
  hourly
    ? d.toLocaleTimeString([], { hour: "numeric" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
const dayWord = (b) => `${b} day${b === "1" ? "" : "s"}`;
const show = (v) => (v === true ? "yes" : v === false ? "no" : v == null || v === "" ? "—" : String(v));

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Tooltip: any element with data-tip; one fixed div follows the mouse.
// `ready` re-runs the effect when the loading root is replaced by the loaded
// one — they are different elements (#596 review 4).
function useTooltip(rootRef, ready) {
  const [tip, setTip] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const move = (e) => {
      const t = e.target.closest?.("[data-tip]");
      setTip(t ? { text: t.dataset.tip, x: e.clientX + 12, y: e.clientY + 14 } : null);
    };
    const leave = () => setTip(null);
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => {
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
    };
  }, [rootRef, ready]);
  return tip;
}

// Owns the tooltip state, so a mouse move re-renders only this, not the whole
// viewer with its 500-row table (#596 review 5).
// It measures itself and stays on screen: clamped left/right, and flipped ABOVE the
// cursor when it would run off the bottom — the long help texts sit low on the
// detail cards (#605 review).
function Tooltip({ rootRef, ready }) {
  const tip = useTooltip(rootRef, ready);
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!tip) {
      setPos(null); // re-measure from scratch on the next hover
      return;
    }
    if (!ref.current) return;
    const { width, height } = ref.current.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(margin, Math.min(tip.x, window.innerWidth - width - margin));
    const below = tip.y;
    const above = tip.y - height - 24;
    const top = below + height + margin > window.innerHeight && above >= margin ? above : Math.max(margin, Math.min(below, window.innerHeight - height - margin));
    setPos({ left, top });
  }, [tip]);
  if (!tip) return null;
  return (
    <div ref={ref} className="jev-tip" style={pos ? { left: pos.left, top: pos.top } : { left: tip.x, top: tip.y, visibility: "hidden" }}>
      {tip.text}
    </div>
  );
}

// Rounded-top bar path.
const barPath = (x, base, top, w) => {
  const hh = base - top;
  return `M${x},${base} V${top + Math.min(4, hh)} q0,-4 4,-4 h${Math.max(0, w - 8)} q4,0 4,4 V${base} Z`;
};

function BarChart({ bins, color = "var(--jev-series-1)" }) {
  if (!bins.length || bins.every((b) => !b.value)) return <div className="jev-empty">No data yet</div>;
  const W = 340;
  const h = 150;
  const pad = { l: 30, r: 6, t: 8, b: 22 };
  const iw = W - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const max = Math.max(...bins.map((b) => b.value));
  const step = iw / bins.length;
  const bw = Math.max(2, step - 2);
  const y = (v) => pad.t + ih - (v / max) * ih;
  const every = Math.ceil(bins.length / Math.max(1, Math.floor(iw / 48)));
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} role="img">
      {[...new Set([0, Math.round(max / 2), max])].map((t) => (
        <g key={t}>
          <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--jev-grid)" />
          <text x={pad.l - 4} y={y(t) + 4} textAnchor="end">{t}</text>
        </g>
      ))}
      {bins.map((b, i) => {
        const x = pad.l + i * step + 1;
        return (
          <g key={i}>
            {b.value > 0 ? <path d={barPath(x, pad.t + ih, y(b.value), bw)} fill={color} /> : null}
            <rect x={x - 1} y={pad.t} width={step} height={ih} fill="transparent" data-tip={b.tip || `${b.label}: ${b.value}`} />
            {i % every === 0 ? (
              <text x={x + bw / 2} y={h - 6} textAnchor="middle">{b.label}</text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

// Grouped bars: Jev vs the reference table, per bucket.
function CompareChart({ p, refAnswer, officeAnswer }) {
  const W = 300;
  const h = 150;
  const pad = { l: 30, r: 6, t: 14, b: 22 };
  const iw = W - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const g = iw / 4;
  const series = [
    [p, "var(--jev-series-1)", "Jev"],
    [refAnswer, "var(--jev-series-2)", "Table"],
  ].concat(officeAnswer ? [[officeAnswer, "var(--jev-muted)", "This office"]] : []);
  const bw = (g - 4 - 2 * series.length) / series.length;
  const y = (v) => pad.t + ih - v * ih;
  return (
    <>
      <svg width="100%" viewBox={`0 0 ${W} ${h}`} role="img">
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--jev-grid)" />
            <text x={pad.l - 4} y={y(t) + 4} textAnchor="end">{t * 100}%</text>
          </g>
        ))}
        {BUCKETS.map((b, i) => (
          <g key={b}>
            {series.map(([src, c, n], j) => [src?.[b], c, n, j]).map(([v, c, n, j]) => {
              if (v == null) return null;
              const x = pad.l + i * g + 4 + j * (bw + 2);
              return (
                <g key={n}>
                  {v > 0.004 ? <path d={barPath(x, pad.t + ih, y(v), bw)} fill={c} /> : null}
                  <rect x={x} y={pad.t} width={bw} height={ih} fill="transparent" data-tip={`${n}, ${dayWord(b)}: ${pct(v, 1)}`} />
                  {v >= 0.1 ? (
                    <text x={x + bw / 2} y={y(v) - 3} textAnchor="middle">{Math.round(v * 100)}</text>
                  ) : null}
                </g>
              );
            })}
            <text x={pad.l + i * g + g / 2} y={h - 6} textAnchor="middle">{dayWord(b)}</text>
          </g>
        ))}
      </svg>
      <div className="jev-legend">
        <span><i style={{ background: "var(--jev-series-1)" }} />Jev</span>
        <span><i style={{ background: "var(--jev-series-2)" }} />Reference table (no AI)</span>
        {officeAnswer ? <span><i style={{ background: "var(--jev-muted)" }} />This office (no AI)</span> : null}
      </div>
    </>
  );
}

function JsonTree({ value, name, depth = 0 }) {
  const key = name != null ? <span>{String(name)}: </span> : null;
  if (value === null || typeof value !== "object") {
    const cls = typeof value === "string" ? "s" : typeof value === "number" ? "num" : "b";
    return (
      <div className="row">
        {key}
        <span className={cls}>{typeof value === "string" ? JSON.stringify(value) : String(value)}</span>
      </div>
    );
  }
  const arr = Array.isArray(value);
  const ents = arr ? value.map((x, i) => [i, x]) : Object.entries(value);
  return (
    <details open={depth < 1}>
      <summary>
        {key}
        {arr ? `[${ents.length}]` : `{${ents.length}}`}
      </summary>
      {ents.map(([k, v]) => (
        <JsonTree key={k} value={v} name={k} depth={depth + 1} />
      ))}
    </details>
  );
}

function JsonBlock({ title, obj }) {
  const ref = useRef(null);
  const [copied, setCopied] = useState("");
  const setAll = (open) => ref.current?.querySelectorAll("details").forEach((d) => (d.open = open));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      setCopied("Copied");
    } catch (e) {
      setCopied("Copy failed");
    }
    setTimeout(() => setCopied(""), 1200);
  };
  return (
    <div className="jev-card">
      <h3 className={CARD_HELP[title] ? "jev-help" : undefined} data-tip={CARD_HELP[title] || undefined}>{title}</h3>
      <div className="jev-controls" style={{ marginBottom: 6 }}>
        <button type="button" className="jev-btn" onClick={() => setAll(true)}>Expand all</button>
        <button type="button" className="jev-btn" onClick={() => setAll(false)}>Collapse all</button>
        <button type="button" className="jev-btn" onClick={copy}>{copied || "Copy JSON"}</button>
      </div>
      <div className="jev-json" ref={ref}>
        <JsonTree value={obj ?? null} />
      </div>
    </div>
  );
}

const Facts = ({ rows }) => (
  <dl>
    {rows.map(([a, b]) => (
      <React.Fragment key={a}>
        <dt className={FIELD_HELP[a] ? "jev-help" : undefined} data-tip={FIELD_HELP[a] || undefined}>
          {a}
        </dt>
        <dd>{show(b)}</dd>
      </React.Fragment>
    ))}
  </dl>
);

function Detail({ r }) {
  const j = jobOf(r);
  const d = daysAnswer(r);
  const sp = r.request?.state?.similar_past_jobs;
  // `_key` is the window's own row key; the log row keeps its REAL Mongo `_id` (#601 review 2).
  const { _now, _key, _coll, _grade, ...raw } = r;
  const grade = _grade;
  const ranked = Array.isArray(r.crewRanking?.ranked) ? r.crewRanking.ranked : null;
  const assigned = grade?.crew?.assigned || null;
  const fav = r.fav || {};
  const usage = r.response?.usage;
  return (
    <div>
      <h3>
        {r.kellerId}{" "}
        <span className="jev-sub">
          {d ? `Jev: ${dayWord(String(d.choice))} · confidence ${d.confidence} · P(>1 day) ${pct(pMulti(r))}` : r.status}
        </span>
      </h3>
      <div className="jev-detail">
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CARD_HELP["Jev vs the reference table"]}>Jev vs the reference table</h3>
          {d || r.referenceAnswer ? (
            <CompareChart p={d?.probabilities} refAnswer={r.referenceAnswer} officeAnswer={r.officeReferenceAnswer} />
          ) : (
            <div className="jev-empty">No answer ({r.status})</div>
          )}
        </div>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CARD_HELP["The call"]}>The call</h3>
          <Facts
            rows={[
              ["Keller ID", r.kellerId],
              ["When", fmtTime(whenOf(r))],
              ["Mode", `${r.mode}${r.developerMode ? " · developerMode" : ""}`],
              ["Status", `${r.status}${r.reason ? ` (${r.reason})` : ""}`],
              ["Latency", r.latencyMs != null ? `${r.latencyMs} ms` : null],
              ["Model", r.response?.model],
              ["Lambda / table", `${r.lambdaVersion} · ${r.referenceTable}`],
              ["Tokens", usage ? `${usage.input_tokens} in · ${usage.output_tokens} out` : null],
            ]}
          />
        </div>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CARD_HELP["What Jev was told about the job"]}>What Jev was told about the job</h3>
          <Facts
            rows={[
              ["Floor type", j.floor_type],
              ["More than one floor type", j.more_than_one_floor_type],
              ["Labour $", j.labour_dollars != null ? j.labour_dollars.toLocaleString("en-US") : null],
              ["Labour band", j.labour_dollars_band_for_this_floor_type],
              ["Office", j.office],
              ["Rush / expedite", j.rush_or_expedite],
              ["Similar past jobs", sp ? `${sp.count?.toLocaleString("en-US")} jobs` : null],
            ]}
          />
        </div>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CARD_HELP["What FAV did"]}>What FAV did</h3>
          <Facts
            rows={[
              ["Reserve requested", fav.reserveRequested],
              ["Reserved", fav.reserved],
              ["Date", fav.reservedDate],
              ["Booked working days", fav.bookedWorkingDays],
              ["Skip reason", fav.reserveSkippedReason],
              ["Degraded", fav.degraded],
            ]}
          />
          <h3 style={{ marginTop: 12 }} className="jev-help" data-tip={CARD_HELP["The job now"]}>The job now</h3>
          <Facts
            rows={
              !_now || _now.gone
                ? [["Now", "no longer in ActiveQueue"]]
                : [
                    ["Status code", _now.statusCode],
                    ["Scheduled", _now.scheduled],
                    ["Completion", _now.completion],
                    ["Booked working days now", _now.bookedWorkingDays],
                    ["Crew assigned", _now.crewAssigned],
                    ["Finished", _now.finished ? "yes, gradable" : "not yet"],
                  ]
            }
          />
        </div>
      </div>
      <div className="jev-detail" style={{ marginTop: 12 }}>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CARD_HELP["Outcome"]}>Outcome</h3>
          {!grade ? (
            <div className="jev-empty">Not graded yet</div>
          ) : !grade.final ? (
            <Facts
              rows={[
                ["Graded", [20, 21].includes(Number(grade.jobStatus)) ? "no — the job was cancelled / written off" : "pending — the job hasn't finished"],
                ["Job status", grade.jobStatus],
                ["Last checked", grade.gradedAt ? fmtTime(new Date(grade.gradedAt)) : null],
              ]}
            />
          ) : (
            <Facts
              rows={[
                ["Actual working days", grade.actual?.workingDays],
                ["Start → completion", `${grade.actual?.start ?? "—"} → ${grade.actual?.completion ?? "—"}`],
                ["Installed", grade.actual?.installed],
                ["FAV booked", `${grade.favBookedDays ?? 1} day${grade.favBookedDays === 1 ? "" : "s"}`],
                ["Ran longer than booked", grade.ranLongerThanBooked],
                ["Jev's day choice right", grade.days ? (grade.days.hit ? "yes ✓" : "no ✗") : null],
                ["Jev's chance of what happened", grade.days ? pct(grade.days.pActual) : null],
                ["Multi-day yes/no", grade.multiDay ? `Jev ${pct(grade.multiDay.p)} · actual ${grade.multiDay.actual ? "yes" : "no"}` : null],
                ["Crew lead", grade.actual?.crewLead],
              ]}
            />
          )}
          {crewLine(grade) ? <div className="jev-note" style={{ marginTop: 8 }}>{crewLine(grade)}</div> : null}
        </div>
        <div className="jev-card">
          <h3><span className="jev-help" data-tip={CARD_HELP["Crews free that day"]}>Crews free that day</span> <span className="jev-sub">{r.crewRanking?.date || ""}</span></h3>
          {ranked && ranked.length ? (
            <table>
              <thead>
                <tr>
                  {["#", "Crew", "Days (1/2/3/4+)", "Suggested", "P(>1 day)", "Capacity days", "Shared from", "Flags"].map((h) => (
                    <th key={h} className="jev-help" data-tip={CREW_COLUMN_HELP[h]}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ranked.map((c) => (
                  <tr key={c.payrollNo} className={assigned && c.payrollNo === assigned ? "sel" : ""}>
                    <td>{c.rank}</td>
                    <td>{c.payrollNo}{assigned && c.payrollNo === assigned ? " ← assigned" : ""}</td>
                    <td>
                      {c.days ? (
                        <span className="jev-pbar">
                          {BUCKETS.map((b, i) => (
                            <span key={b} style={{ width: `${100 * (c.days[b] || 0)}%`, background: BUCKET_COLORS[i] }} data-tip={`${dayWord(b)}: ${pct(c.days[b] || 0)}`} />
                          ))}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{c.suggestedDays ? dayWord(String(c.suggestedDays)) : "—"}</td>
                    <td>{pct(c.pMultiDay, 1)}</td>
                    <td>{c.capacityDays ?? "—"}</td>
                    <td>{c.sharedFrom || "—"}</td>
                    <td>{(c.flags || []).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="jev-empty">{r.crewRanking?.skipReason ? `No ranking (${r.crewRanking.skipReason})` : "No crew ranking on this call"}</div>
          )}
        </div>
      </div>
      <div className="jev-detail" style={{ marginTop: 12 }}>
        <JsonBlock title="Request sent to Jev" obj={r.request} />
        <JsonBlock title="Jev's response" obj={r.response} />
      </div>
      <div style={{ marginTop: 12 }}>
        <JsonBlock title="Full log row" obj={raw} />
        {grade ? <div style={{ marginTop: 12 }}><JsonBlock title="Grade" obj={grade} /></div> : null}
      </div>
    </div>
  );
}

const COLUMNS = [
  ["at", "Time"],
  ["kellerId", "Keller ID"],
  ["status", "Status"],
  ["floor", "Floor"],
  ["labour", "Labour $"],
  ["choice", "Jev"],
  ["pmulti", "Jev probabilities"],
  ["pmulti", "P(>1 day)"],
  ["ref", "Table P(>1)"],
  [null, "FAV booked"],
  ["now", "Booking now"],
  ["actual", "Actual"],
  ["latencyMs", "ms"],
];

function CallRow({ r, selected, onSelect }) {
  const [c, icon] = STATUS_STYLE[r.status] || ["var(--jev-muted)", "?"];
  const d = daysAnswer(r);
  const p = d?.probabilities;
  const j = jobOf(r);
  const now = r._now;
  return (
    <tr className={selected ? "sel" : ""} onClick={() => onSelect(r._key)}>
      <td>
        {fmtTime(whenOf(r))}
        {r._coll === "JevReplayLog" ? <span className="jev-note"> replay</span> : null}
      </td>
      <td>{r.kellerId}</td>
      <td>
        <span className="jev-dot" style={{ background: c }} />
        {icon} {r.status}
        {r.reason ? <span className="jev-note"> {r.reason}</span> : null}
      </td>
      <td>{j.floor_type || "—"}</td>
      <td>{j.labour_dollars != null ? j.labour_dollars.toLocaleString("en-US") : "—"}</td>
      <td>{d ? <><b>{d.choice}</b> {d.choice === "1" ? "day" : "days"}</> : "—"}</td>
      <td>
        {p ? (
          <span className="jev-pbar">
            {BUCKETS.map((b, i) => (
              <span key={b} style={{ width: `${100 * (p[b] || 0)}%`, background: BUCKET_COLORS[i] }} data-tip={`${dayWord(b)}: ${pct(p[b] || 0)}`} />
            ))}
          </span>
        ) : null}
      </td>
      <td>{pct(pMulti(r))}</td>
      <td>{pct(refPMulti(r))}</td>
      <td>
        {r.fav?.reserved
          ? `${r.fav.reservedDate} · ${r.fav.bookedWorkingDays ?? 1}d`
          : r.fav?.reserveSkippedReason
          ? "not reserved"
          : "—"}
      </td>
      <td>
        {!now
          ? "—"
          : now.gone
          ? "gone"
          : `${now.bookedWorkingDays ? `${now.bookedWorkingDays}d` : "unscheduled"} · s${now.statusCode ?? "?"}${now.finished ? " ✓ finished" : ""}`}
      </td>
      <td>
        {(() => {
          const g = gradeCell(r._grade);
          return g.hit === null ? g.text : <>{g.text} {g.hit ? "✓" : "✗"}</>;
        })()}
      </td>
      <td>{r.latencyMs ?? "—"}</td>
    </tr>
  );
}

export default function JevViewer() {
  const [includeReplays, setIncludeReplays] = useState(false);
  const result = useConvexQuery(refs.jevListRecent, { includeReplays });
  // Accuracy over EVERY grade — an action (it pages the grades), so it is polled,
  // not reactive. Grades change hourly overnight; every 5 minutes is plenty.
  const runSummary = useAction(refs.jevGradeSummary);
  const [summary, setSummary] = useState(undefined);
  useEffect(() => {
    let alive = true;
    const load = () =>
      runSummary({})
        .then((s) => alive && setSummary(s))
        .catch(() => alive && setSummary(null));
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [runSummary]);
  const rootRef = useRef(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "at", dir: -1 });
  const [selectedId, setSelectedId] = useState(null);

  const all = useMemo(
    () =>
      (result?.rows || []).map((x) => ({
        ...decodeConvexDoc(x.data),
        _id: x.data?._id ?? x.id, // the real Mongo _id (the mirror stores it in data)
        _key: `${x.coll}:${x.id}`, // unique across live + replay rows
        _coll: x.coll,
        _now: x.now,
        _grade: x.grade ? decodeConvexDoc(x.grade) : null,
      })),
    [result]
  );
  const rows = useMemo(() => filterRows(all, { status, search }), [all, status, search]);
  const sorted = useMemo(() => sortRows(rows, sort.key, sort.dir), [rows, sort]);
  const selected = useMemo(() => all.find((r) => r._key === selectedId) || null, [all, selectedId]);
  const tiles = useMemo(
    () => kpiTiles(rows, fmtTime).concat(gradeTiles(summary, includeReplays)),
    [rows, summary, includeReplays]
  );
  const bins = useMemo(
    () => ({ time: timeBins(rows, binLabel, fmtTime), pmulti: pMultiBins(rows), latency: latencyBins(rows) }),
    [rows]
  );

  // Deep link once data arrives: #detail (newest) or #<KellerID>.
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current || !all.length) return;
    linked.current = true;
    const r = rowForHash(all, window.location.hash);
    if (r) setSelectedId(r._key);
  }, [all]);

  if (result === undefined) {
    return (
      <div className="jev-root" ref={rootRef}>
        <div className="jev-empty">Loading Jev calls…</div>
      </div>
    );
  }

  const onSort = (key) => {
    if (!key) return;
    setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));
  };

  return (
    <div className="jev-root" ref={rootRef} style={{ display: "grid", gap: 12 }}>
      <div className="jev-controls">
        <label>
          Status{" "}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <input type="search" placeholder="Keller ID / floor / office" size={24} value={search} onChange={(e) => setSearch(e.target.value)} />
        <label>
          <input type="checkbox" checked={includeReplays} onChange={(e) => setIncludeReplays(e.target.checked)} /> Include replays
        </label>
        <span className="jev-note">
          Live · newest {result.limit} calls{result.truncated ? " (older calls not shown)" : ""}
          {includeReplays ? ` · + newest ${result.replays} replays` : ""}
        </span>
      </div>

      <section className="jev-kpis">
        {tiles.map((t) => (
          <div className="jev-card jev-kpi" key={t.label} data-tip={tileHelp(t.label) || undefined}>
            <div className="l">
              <span className={tileHelp(t.label) ? "jev-help" : undefined}>{t.label}</span>
            </div>
            <div className="v">{t.value}</div>
            <div className="n">{t.note}</div>
          </div>
        ))}
      </section>

      <section className="jev-charts">
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CHART_HELP["Calls over time"]}>Calls over time</h3>
          <BarChart bins={bins.time} />
        </div>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CHART_HELP["Jev's chance a job takes more than 1 day"]}>Jev's chance a job takes more than 1 day</h3>
          <BarChart bins={bins.pmulti} />
          <div className="jev-note">Answered calls only. FAV books every job as 1 day today.</div>
        </div>
        <div className="jev-card">
          <h3 className="jev-help" data-tip={CHART_HELP["Latency (job read + Jev)"]}>Latency (job read + Jev)</h3>
          <BarChart bins={bins.latency} />
        </div>
      </section>

      <section className="jev-card">
        <h3>
          Calls <span className="jev-sub">· {rows.length} shown · click a column to sort, a row for detail</span>
        </h3>
        <div className="jev-tablewrap">
          <table>
            <thead>
              <tr>
                {COLUMNS.map(([k, label], i) => (
                  <th key={i} onClick={() => onSort(k)} data-tip={COLUMN_HELP[label]}>
                    <span className="jev-help">{label}</span>
                    {k && sort.key === k && label !== "Jev probabilities" ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length ? (
                sorted.map((r) => <CallRow key={r._key} r={r} selected={r._key === selectedId} onSelect={setSelectedId} />)
              ) : (
                <tr>
                  <td colSpan={COLUMNS.length} className="jev-empty">
                    {all.length ? "No calls match." : "No Jev calls yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="jev-legend">
          {BUCKETS.map((b, i) => (
            <span key={b}>
              <i style={{ background: BUCKET_COLORS[i] }} />
              {dayWord(b)}
            </span>
          ))}
          <span>· bar = Jev's probability for each</span>
        </div>
      </section>

      <section className="jev-card">
        {selected ? <Detail r={selected} /> : <div className="jev-empty">Select a call above to see everything about it.</div>}
      </section>

      <Tooltip rootRef={rootRef} ready={result !== undefined} />
    </div>
  );
}
